import { useTheme } from "next-themes";
import { Toaster as Sonner, toast } from "sonner";
import { AVISO_MS, conDuracion } from "@/lib/avisoDuracion";

type ToasterProps = React.ComponentProps<typeof Sonner>;

/* CUÁNTO SE QUEDA UN AVISO DE SONNER EN PANTALLA (2026-08-27).

   Sonner no sabe de variantes: su `duration` es UNA para todos los avisos, y
   aquí hacen falta dos —el acierto es un parpadeo y el error hay que poder
   leerlo—. Así que la duración por defecto de la etiqueta es la del acierto, y
   a las funciones de error se les pone la suya al vuelo.

   Se parchea el objeto `toast` que exporta la librería porque es la ÚNICA
   forma de alcanzar a los 199 sitios que llaman a `toast.error`: los 62
   archivos que lo usan importan de "sonner" directamente, así que envolverlo
   solo en este archivo no llegaría a ninguno. `toast` es un objeto con
   propiedades normales (la librería lo monta con `Object.assign`) y todos los
   módulos comparten la MISMA referencia, así que cambiarlas aquí los cubre a
   todos.

   Esto corre al importar este archivo, y este archivo lo importa `App`, así
   que está hecho mucho antes de que nadie pueda lanzar un aviso.

   Quien quiera otra duración la sigue mandando en su llamada: las opciones de
   quien llama pisan a las de aquí. */
type FnAviso = (mensaje: never, opciones?: { duration?: number }) => string | number;

// El guardia del doble parcheo: sin él, un recambio en caliente durante el
// desarrollo envolvería lo ya envuelto una y otra vez.
const yaParcheado = "__ulturePatched" as const;
const marcable = toast as unknown as Record<string, unknown>;
if (!marcable[yaParcheado]) {
  marcable[yaParcheado] = true;
  for (const [nombre, ms] of [
    ["error", AVISO_MS.error],
    ["warning", AVISO_MS.error],
    ["success", AVISO_MS.ok],
    ["info", AVISO_MS.ok],
    ["message", AVISO_MS.ok],
  ] as const) {
    // `loading` y `promise` se quedan como están: uno vive hasta que lo
    // sustituyen y el otro maneja sus tiempos por su cuenta.
    marcable[nombre] = conDuracion(marcable[nombre] as FnAviso, ms);
  }
}

const Toaster = ({ ...props }: ToasterProps) => {
  const { theme = "system" } = useTheme();

  return (
    <Sonner
      theme={theme as ToasterProps["theme"]}
      className="toaster group"
      duration={AVISO_MS.ok}
      toastOptions={{
        classNames: {
          toast:
            "group toast group-[.toaster]:bg-background group-[.toaster]:text-foreground group-[.toaster]:border-border group-[.toaster]:shadow-lg",
          description: "group-[.toast]:text-muted-foreground",
          actionButton: "group-[.toast]:bg-primary group-[.toast]:text-primary-foreground",
          cancelButton: "group-[.toast]:bg-muted group-[.toast]:text-muted-foreground",
        },
      }}
      {...props}
    />
  );
};


export { Toaster, toast };
